import type { QuestionComponentConfig } from './questionComponentFactory.types';

/**
 * Map from variant question_type values (legacy or specialised) onto the
 * canonical key registered with the factory. Values are lowercased before
 * lookup so any case variant is also handled.
 */
const QUESTION_TYPE_ALIASES: Record<string, string> = {
  team_pick: 'team',
  team_over_under: 'over_under',
  player_over_under: 'over_under',
  // Main markets (DKPLAT-500): Moneyline/Spread render as team picks (2–3 options,
  // incl. a logo-less Draw tile); Total renders as an Over/Under pick.
  moneyline: 'team',
  spread: 'team',
  total: 'over_under',
  // Outcome markets (DKPLAT-871): a list of text options ("KO/TKO/DQ", "Hendrick Motorsports",
  // "Mertens 2:1") with one correct — exactly what the Custom card draws.
  outcome: 'custom',
};

function normaliseQuestionType(questionType: string): string {
  if (!questionType) return questionType;
  const lower = questionType.toLowerCase();
  return QUESTION_TYPE_ALIASES[lower] ?? lower;
}

/**
 * Factory class for managing question component registration and lookup
 *
 * Implements the Factory pattern to decouple question type routing
 * from component rendering. Components are registered with their
 * adapter functions, then looked up by question type at render time.
 *
 * @example
 * ```ts
 * // Register a component
 * questionComponentFactory.register(QUESTION_TYPES.TEAM, {
 *   component: TeamPick,
 *   adapter: (context) => ({ ...props })
 * });
 *
 * // Look up a component
 * const config = questionComponentFactory.get(QUESTION_TYPES.TEAM);
 * const Component = config.component;
 * const props = config.adapter(context);
 * ```
 */
class QuestionComponentFactory {
  /** Internal registry mapping question types to component configs */
  private registry = new Map<string, QuestionComponentConfig>();

  /**
   * Registers a question component with its adapter
   *
   * @param questionType - The question type identifier (e.g., 'team', 'prop')
   * @param config - The component and adapter configuration
   *
   * @example
   * ```ts
   * factory.register('team', {
   *   component: TeamPick,
   *   adapter: (context) => ({ ...teamPickProps })
   * });
   * ```
   */
  register(questionType: string, config: QuestionComponentConfig): void {
    if (this.registry.has(questionType)) {
      console.warn(
        `[QuestionComponentFactory] Overwriting existing registration for type: ${questionType}`
      );
    }
    this.registry.set(questionType, config);
  }

  /**
   * Retrieves a component configuration by question type.
   *
   * The lookup is normalised so that legacy / specialised values stored
   * in older Pick'ems still route to the right renderer:
   *   - case-insensitive (TEAM, Team, team all match)
   *   - 'team_pick' → 'team'
   *   - 'team_over_under' / 'player_over_under' → 'over_under'
   *
   * The backend response schema accepts those variants but the factory
   * only registers the six canonical types. Without this normalisation a
   * question authored with one of the variant values would render as
   * nothing.
   *
   * @param questionType - The question type to look up
   * @returns The component config, or undefined if not registered
   */
  get(questionType: string): QuestionComponentConfig | undefined {
    return this.registry.get(normaliseQuestionType(questionType));
  }

  /**
   * Checks if a question type has been registered
   *
   * @param questionType - The question type to check
   * @returns True if registered, false otherwise
   *
   * @example
   * ```ts
   * if (factory.has('team')) {
   *   // Handle team question
   * }
   * ```
   */
  has(questionType: string): boolean {
    return this.registry.has(normaliseQuestionType(questionType));
  }

  /**
   * Gets all registered question types
   *
   * Useful for debugging or testing to verify which types are registered.
   *
   * @returns Array of question type identifiers
   *
   * @example
   * ```ts
   * const types = factory.getSupportedTypes();
   * console.log('Registered question types:', types);
   * // ['team', 'player', 'prop', 'tiebreaker']
   * ```
   */
  getSupportedTypes(): string[] {
    return Array.from(this.registry.keys());
  }

  /**
   * Clears all registered components
   *
   * Primarily useful for testing. In production, registrations
   * happen once at app startup and persist.
   *
   * @example
   * ```ts
   * // In tests
   * afterEach(() => {
   *   factory.clear();
   * });
   * ```
   */
  clear(): void {
    this.registry.clear();
  }
}

/**
 * Singleton instance of the question component factory
 *
 * Import and use this instance throughout the application.
 * Components are registered once at app startup via the
 * questionComponentRegistry module.
 *
 * @example
 * ```ts
 * import { questionComponentFactory } from '@/factories/questionComponentFactory';
 *
 * const config = questionComponentFactory.get(questionType);
 * ```
 */
export const questionComponentFactory = new QuestionComponentFactory();
